import {
  Box,
  Chip,
  FormControlLabel,
  Grid2 as Grid,
  Switch,
  Typography,
} from "@mui/material";
import { FC, useEffect } from "react";
import {
  Controller,
  FieldErrors,
  useFormContext,
  useWatch,
} from "react-hook-form";
import { useGetPartnerDropdownsQuery } from "src/shared/api/dropdowns/partner";
import {
  AutocompleteField,
  ContentAccordion,
  SelectField,
} from "src/shared/components";
import { agenciesCountry } from "src/shared/constants/company.constants";
import { useManagerSearch } from "src/shared/hooks/useManagerSearch";
import { EAgencies } from "src/shared/types/common.types";
import { EOrganizationValue } from "src/shared/types/companies.types";
import { messages } from "src/shared/utils/hookFormRules";
import { IPartnerFormValues } from "../../model/types";
import {
  IEditPartnerFormValues,
  IPartnerBaseFormValues,
} from "../../model/types/partner";

export const GeneralInfo: FC = () => {
  const { data: dropdowns } = useGetPartnerDropdownsQuery();

  const { country, agency, organization_form, ownership } = dropdowns || {};

  const {
    control,
    setValue,
    trigger,
    formState: { errors },
  } = useFormContext<IPartnerFormValues | IEditPartnerFormValues>();

  const partnerAgency = useWatch({
    control,
    name: "partner.agency",
  });

  const registrationCountry = useWatch({
    control,
    name: "partner.registration_country_id",
  });

  const mainManagerId = useWatch({
    control,
    name: "partner.main_manager_id",
  });

  const { autocompleteProps } = useManagerSearch({
    mainManagerId,
  });

  const handleOrgFormChange = (form: EOrganizationValue | null) => {
    switch (form) {
      case EOrganizationValue.IndividualEntrepreneur: {
        const individualInterprenerOwnershipValue = ownership?.find(
          ({ name }) => name.toLowerCase() === "индивидуальный предприниматель",
        );

        setValue(
          "partner.ownership_id",
          individualInterprenerOwnershipValue?.value || 8,
        );
        break;
      }

      case EOrganizationValue.Individual: {
        const individualOwnershipValue = ownership?.find(
          ({ name }) => name.toLowerCase() === "физическое лицо",
        );

        setValue("partner.ownership_id", individualOwnershipValue?.value || 25);
        break;
      }

      case EOrganizationValue.LegalEntity: {
        setValue("partner.ownership_id", "");
        break;
      }

      default: {
        setValue("partner.ownership_id", "");
        break;
      }
    }

    trigger("partner.ownership_id");
  };

  const validatingFields: Array<keyof FieldErrors<IPartnerBaseFormValues>> = [
    "agency",
    "main_manager_id",
    "organization_form",
    "registration_country_id",
  ];

  const hasErrors =
    errors.partner &&
    validatingFields.some((field) => errors.partner?.[field] !== undefined);

  useEffect(() => {
    const agencyCountry =
      partnerAgency !== "" ? agenciesCountry[partnerAgency] : null;

    if (registrationCountry === agencyCountry) {
      setValue("partner.is_resident", true);
    } else {
      setValue("partner.is_resident", false);
    }
  }, [partnerAgency, registrationCountry, setValue]);

  return (
    <ContentAccordion
      defaultExpanded
      title={
        <Box display="flex" alignItems="center">
          <Chip label="1" />
          <Typography
            color={hasErrors ? "error" : "grey.500"}
            sx={{ ml: 2 }}
            component="span"
          >
            Webcom Group
          </Typography>
        </Box>
      }
    >
      <Grid container spacing={4}>
        <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
          <Controller
            control={control}
            name="partner.agency"
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <SelectField<EAgencies>
                disabled
                inputRef={ref}
                fullWidth
                {...field}
                label="Выбор компании"
                error={!!error}
                helperText={error?.message}
                options={agency}
              />
            )}
            rules={{ required: messages.required }}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
          <Controller
            control={control}
            name="partner.main_manager_id"
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <AutocompleteField<number, false>
                fullWidth
                {...field}
                inputRef={ref}
                label="Менеджер"
                error={!!error}
                helperText={error?.message}
                value={field.value || null}
                {...autocompleteProps}
              />
            )}
            rules={{ required: messages.required }}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
          <Controller
            control={control}
            name="partner.organization_form"
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <SelectField<EOrganizationValue | null>
                disabled
                inputRef={ref}
                fullWidth
                {...field}
                onChange={(v) => {
                  field.onChange(v);
                  handleOrgFormChange(
                    v.target.value as EOrganizationValue | null,
                  );
                }}
                label="Вид деятельности"
                value={field.value}
                error={!!error}
                helperText={error?.message}
                options={organization_form}
              />
            )}
            rules={{ required: messages.required }}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
          <Controller
            control={control}
            name="partner.registration_country_id"
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <SelectField
                disabled
                inputRef={ref}
                fullWidth
                {...field}
                label="Страна регистрации"
                error={!!error}
                helperText={error?.message}
                options={country}
              />
            )}
            rules={{ required: messages.required }}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
          <Controller
            name="partner.is_resident"
            render={({ field }) => (
              <FormControlLabel
                disabled
                control={<Switch {...field} checked={!!field.value} />}
                label="Резидент"
              />
            )}
          />
        </Grid>
      </Grid>
    </ContentAccordion>
  );
};
